import { Link } from "react-router-dom";
import { Gift, ExternalLink, ChevronRight, Flame, Award, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { StarRating } from "./StarRating";

export default function CasinoCard({ casino, featured = false }) {
  const {
    name, slug, logo, rating, bonus_title, bonus_amount, bonus_description,
    free_spins, wagering, is_featured, is_trending, review_count, website_url,
  } = casino;
  const hasBonus = Boolean(bonus_title || bonus_amount || bonus_description || free_spins);

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-[#0b1222] transition duration-300 hover:-translate-y-1 hover:border-violet-400/50 hover:shadow-xl hover:shadow-violet-950/30 ${
        featured ? "border-violet-400/50 ring-1 ring-violet-400/15" : "border-white/10"
      }`}
      data-testid={`casino-card-${slug}`}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400 to-transparent opacity-0 transition group-hover:opacity-100" />
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-slate-900 p-1.5 sm:h-16 sm:w-16">
            {logo ? <img src={logo} alt={`${name} logo`} className="h-full w-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="text-xl font-extrabold text-violet-300">{name?.slice(0, 1) || "C"}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              {is_featured && <Badge className="flex items-center gap-1 border border-violet-300/20 bg-violet-500/15 px-2 py-0.5 text-[10px] text-violet-200"><Award className="h-3 w-3" /> Featured</Badge>}
              {is_trending && <Badge className="flex items-center gap-1 border border-orange-300/20 bg-orange-500/10 px-2 py-0.5 text-[10px] text-orange-200"><Flame className="h-3 w-3" /> Trending</Badge>}
            </div>
            <h3 className="truncate text-base font-bold text-white transition group-hover:text-violet-200 sm:text-lg">{name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <StarRating rating={rating} size="sm" />
              <span className="text-xs text-slate-400">{review_count?.toLocaleString() || 0} reviews</span>
            </div>
          </div>
          {Number(rating) >= 4.9 && <span className="shrink-0 rounded-full border border-amber-300/20 bg-amber-300/10 px-2 py-1 text-[10px] font-bold text-amber-200">TOP RATED</span>}
        </div>

        <div className="mb-4 flex-1 rounded-xl border border-emerald-300/15 bg-gradient-to-br from-emerald-400/[0.09] to-cyan-400/[0.04] p-3.5">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-200"><Gift className="h-4 w-4 shrink-0" /> Bonus information</div>
          {bonus_title && <p className="mb-1 text-sm font-bold text-white">{bonus_title}</p>}
          {bonus_amount && <p className="text-lg font-extrabold leading-snug text-emerald-300">{bonus_amount}</p>}
          {free_spins && Number(free_spins) > 0 && <p className="mt-1 text-sm font-semibold text-emerald-100">+ {free_spins} Free Spins</p>}
          {bonus_description && <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-300">{bonus_description}</p>}
          {!hasBonus && <p className="text-sm text-slate-300">Visit the casino to review current offers and terms.</p>}
        </div>

        {wagering && <div className="mb-4 flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4 shrink-0 text-cyan-300" /><span>Wagering requirement: <span className="font-medium text-slate-200">{wagering}</span></span></div>}

        <div className="mt-auto grid grid-cols-[1fr_auto] gap-2">
          <a href={website_url || undefined} target="_self" rel="nofollow sponsored" referrerPolicy="origin" className={!website_url ? "pointer-events-none" : ""} aria-label={`Visit ${name}`}>
            <Button disabled={!website_url} className="min-h-11 w-full rounded-full bg-gradient-to-r from-violet-600 to-purple-500 text-sm font-bold text-white shadow-md shadow-violet-950/30 transition hover:brightness-110" data-testid={`visit-casino-${slug}`}>
              Visit Casino <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </a>
          <Link to={`/casinos/${slug}`} aria-label={`Read ${name} review`}>
            <Button variant="outline" className="min-h-11 rounded-full border-white/15 bg-white/[0.03] px-3 text-white hover:border-violet-300/40 hover:bg-violet-500/10" data-testid={`review-casino-${slug}`}><ChevronRight className="h-4 w-4" /></Button>
          </Link>
        </div>
        <p className="mt-3 text-center text-[10px] leading-4 text-slate-500">18+ · Terms apply · Play responsibly</p>
      </div>
    </article>
  );
}
